import { useCallback } from 'react'
import { Text, Canvas } from 'fabric'
import { clampFontSize } from '../../utils/validation'
import { renderCanvas } from '../utils/canvasUtils'
import { convertHexWithAlphaToRgba } from '../../utils/color/colorConversion'
import {
    DEFAULT_FONT_SIZE,
    TEXT_OBJECT_PADDING,
    TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
} from '../../constants'

/**
 * Параметры для хука масштабирования текста
 */
export interface UseTextScalingParams {
    textObjectRef: React.MutableRefObject<Text | null>
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    isScalingFromSliderRef: React.MutableRefObject<boolean>
    onFontSizeChange?: (size: number) => void
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
    hasBackground: boolean
    backgroundColor: string | null
}

/**
 * Хук для обработки изменения размера текста через углы
 * Синхронизирует размер шрифта с ползунком
 */
export function useTextScaling(params: UseTextScalingParams): () => void {
    const {
        textObjectRef,
        fabricCanvasRef,
        isScalingFromSliderRef,
        onFontSizeChange,
        safeArea,
        hasBackground,
        backgroundColor,
    } = params

    const handleTextScaling = useCallback(() => {
        if (
            !textObjectRef.current ||
            !fabricCanvasRef.current ||
            isScalingFromSliderRef.current
        ) {
            return
        }

        const textObj = textObjectRef.current
        const fabricCanvas = fabricCanvasRef.current

        // Сохраняем текущую позицию перед изменением размера
        const currentLeft = textObj.left ?? 0
        const currentTop = textObj.top ?? 0
        const currentFontSize = textObj.fontSize ?? DEFAULT_FONT_SIZE
        const scaleFactor = textObj.scaleX ?? 1

        // Рассчитываем новый размер шрифта
        const newFontSize = currentFontSize * scaleFactor
        const clampedSize = clampFontSize(newFontSize)

        // Рассчитываем максимальную ширину текста с учетом padding
        // width в Fabric.js - это максимальная ширина для переноса строк
        // Нужно обновлять её при изменении размера, чтобы текст не вылезал за границы canvas
        const canvasWidth = fabricCanvas.width
        const borderPadding = TEXT_OBJECT_PADDING.BORDER * 2
        const backgroundPadding =
            hasBackground && backgroundColor !== null
                ? TEXT_OBJECT_PADDING.BACKGROUND * 2
                : 0
        // Максимальная ширина = ширина canvas минус padding (без ограничений safeArea)
        const maxInnerTextWidth = Math.max(
            TEXT_OBJECT_PADDING.MIN_WIDTH,
            canvasWidth - borderPadding - backgroundPadding
        )

        // Обновляем размер шрифта, максимальную ширину и сбрасываем scale, сохраняя позицию
        // width устанавливается как максимальная ширина для переноса, не фиксирует размер рамки
        textObj.set({
            fontSize: clampedSize,
            width: maxInnerTextWidth, // Обновляем максимальную ширину для переноса
            scaleX: 1,
            scaleY: 1,
            left: currentLeft,
            top: currentTop,
        })

        // Пересчитываем размеры текста после изменения fontSize и width
        // Это позволяет Fabric.js автоматически подстроить реальную ширину под содержимое
        // и синхронизировать размеры подложки
        textObj.initDimensions()

        // Принудительно применяем параметры подложки после initDimensions()
        // initDimensions() может сбросить параметры подложки, поэтому нужно их восстановить
        if (hasBackground && backgroundColor !== null) {
            const rgbaBackgroundColor =
                convertHexWithAlphaToRgba(backgroundColor)
            textObj.set({
                textBackgroundColor: rgbaBackgroundColor,
                textBackgroundPadding: TEXT_OBJECT_PADDING.BACKGROUND,
                textBackgroundBorderRadius: TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
            })
        } else {
            textObj.set({
                textBackgroundColor: undefined,
                textBackgroundPadding: undefined,
                textBackgroundBorderRadius: undefined,
            })
        }

        // Принудительно применяем настройки обводки для корректного отображения границ
        // Это обеспечивает одинаковое отображение границ при изменении через слайдер и уголок
        textObj.setCoords()

        // Синхронизируем с ползунком
        onFontSizeChange?.(clampedSize)

        // Перерисовываем canvas
        renderCanvas({ canvas: fabricCanvas, textObj })
    }, [
        textObjectRef,
        fabricCanvasRef,
        isScalingFromSliderRef,
        onFontSizeChange,
        safeArea,
        hasBackground,
        backgroundColor,
    ])

    return handleTextScaling
}
